import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import { cn } from '@rocco/utils/cn';
import type { ProfileFamilyBrand } from '@modules/professional/util/derive-profile-family';

/**
 * Beyond three marks the cluster crowds the hero's edge, so the rest of the
 * family stays to the Brand Family sections further down the page.
 */
const MAX_MARKS = 3;

/**
 * Stacking order, as Tailwind classes rather than an inline `zIndex` so the hover
 * state can lift the hovered mark above its neighbours — an inline style would
 * always beat the hover class.
 *
 * Which end sits on top depends on which side the profile's own badge is: the
 * fan has to recede TOWARDS it, so the mark furthest from the badge paints
 * highest. Marks render left-to-right, so a badge on the right (desktop) wants
 * descending z and a badge on the left (mobile, mirrored) wants ascending.
 */
const MARK_Z = ['z-1', 'z-2', 'z-3'];

interface ProfileFamilyMarksProps {
    brands: ProfileFamilyBrand[];
    /** The row — its position and the overlap (`-space-x-*`) live here. */
    className?: string;
    /** One circle — its size lives here. */
    markClassName?: string;
    /**
     * Which side the profile's own logo badge sits on, so the fan recedes towards
     * it. `right` on desktop (badge bottom-right of the hero, marks to its left);
     * `left` on mobile, where the badge is bottom-left and the marks mirror.
     */
    badgeSide?: 'left' | 'right';
    /**
     * `false` renders plain, decorative circles instead of per-brand links — for
     * the mobile backing line, where the whole line is already one link and
     * nesting anchors would be invalid. The line names the brands, so the stack
     * is hidden from assistive tech there.
     */
    interactive?: boolean;
}

/**
 * The sibling brand marks beside the professional's own logo badge (Figma
 * 5498:66909): one circle per family brand at ~51% of the badge, each
 * overlapping the next. The brand furthest from the badge paints on top, so the
 * stack reads as a fan opening away from the profile it belongs to. Each mark
 * links to that brand's profile.
 */
export const ProfileFamilyMarks = ({
    brands,
    className,
    markClassName,
    badgeSide = 'right',
    interactive = true,
}: ProfileFamilyMarksProps) => {
    const marks = brands.filter((brand) => brand.logo).slice(0, MAX_MARKS);
    if (marks.length === 0) return null;
    // Marks render left-to-right, so a badge on the right needs the order
    // reversed for the furthest mark to end up highest.
    const zOrder = badgeSide === 'right' ? [...MARK_Z].reverse() : MARK_Z;

    return (
        <div
            className={cn('flex items-center', className)}
            aria-hidden={interactive ? undefined : 'true'}
        >
            {marks.map((brand, index) => {
                const logo = (
                    <ProfessionalTileLogo
                        image={{
                            src: brand.logo,
                            alt: interactive ? `${brand.title} logo` : '',
                        }}
                        background={brand.logoBackground}
                        className="size-full"
                        contrastBorder
                    />
                );
                const markClass = cn(
                    'relative block shrink-0',
                    zOrder[index] ?? 'z-1',
                    markClassName,
                );
                if (!interactive) {
                    return (
                        <span key={brand.id} className={markClass}>
                            {logo}
                        </span>
                    );
                }
                return (
                    <RoccoLink
                        key={brand.id}
                        to={brand.link}
                        prefetch="intent"
                        aria-label={brand.title}
                        className={cn(
                            markClass,
                            // Lift and front the hovered mark rather than fading
                            // it: these circles overlap, so any transparency
                            // shows the neighbour through and reads as a
                            // rendering fault.
                            'transition-transform duration-200 ease-out hover:z-10 hover:scale-[1.08] focus-visible:z-10 motion-reduce:transition-none motion-reduce:hover:scale-100',
                        )}
                    >
                        {logo}
                    </RoccoLink>
                );
            })}
        </div>
    );
};
